import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material'
import React from 'react'
import { type FormState, type FieldValues } from 'react-hook-form'
import { type BlockerFunction, useBlocker } from 'react-router-dom'

type WithFormStateProp = {
  formState: Pick<FormState<FieldValues>, 'isDirty' | 'isSubmitting'>
  compareFn?: never
}

type WithCompareFnProp = {
  formState?: never
  compareFn: BlockerFunction
}

/**
 * Determines whether navigation should be gated by a confirmation dialog.
 * Accepts either a RHF FormState partial or a custom BlockerFunction to use for comparison.
 */
export const PromptDialog: React.FC<
  { reset: () => void } & (WithFormStateProp | WithCompareFnProp)
> = ({ formState, compareFn, reset }) => {
  const blocker = useBlocker(
    compareFn
      ? compareFn
      : ({ currentLocation, nextLocation }) =>
          currentLocation.pathname !== nextLocation.pathname &&
          (formState.isDirty || formState.isSubmitting)
  )

  return (
    <Dialog
      data-test-prompt-dialog
      aria-labelledby="alert-dialog-title"
      open={blocker.state === 'blocked'}
    >
      <DialogTitle color="error" id="alert-dialog-title" variant="h6">
        Unsaved Changes
      </DialogTitle>
      <DialogContent>
        <DialogContentText color="text" variant="body1">
          Continuing will ignore your unsaved changes.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          data-test-prompt-dialog-cancel
          color="secondary"
          variant="contained"
          onClick={blocker.reset}
        >
          Cancel
        </Button>
        <Button
          autoFocus
          data-test-prompt-dialog-continue
          variant="contained"
          onClick={() => {
            reset()
            blocker.proceed?.()
          }}
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  )
}
